import { startOfMonth, getDay, addDays, subDays, isBefore, isAfter, startOfDay } from 'date-fns';

/** Calendar view state — which grid/panel is currently shown. */
export type CalendarView = 'days' | 'months' | 'years';

/** Structured time value for the TimePanel. */
export interface TimeValue {
  /** 1–12 */
  hours: number;
  /** 0–59 */
  minutes: number;
  period: 'AM' | 'PM';
}

/** Translation keys for weekday abbreviations — resolve via t(`weekdays.${key}`). */
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** Translation keys for full weekday names — resolve via t(`weekdaysFull.${key}`). */
export const WEEKDAY_FULL_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** Translation keys for month names — resolve via t(`months.${key}`). */
export const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

/** Number of columns (days per week) in the calendar grid. */
export const COLS = 7;

/** Pixel gap between trigger and dropdown (also the minimum margin from a viewport edge). */
export const GAP = 4;

/** Max desired popup height (the Figma frame). The popup caps to the available
 *  viewport space when there isn't room for the full frame, so it never clips. */
export const CALENDAR_FRAME_HEIGHT = 395;

export interface CalendarDropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
  /** Pinned popup width — already clamped to the viewport. */
  width: number;
  /** Height the popup may occupy on the chosen side (calendar grid + time list scroll within it). */
  maxHeight: number;
}

interface ComputePositionInput {
  anchorRect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>;
  viewportWidth: number;
  viewportHeight: number;
  baseMinWidth: number;
  /** Max desired height before capping to the viewport (defaults to CALENDAR_FRAME_HEIGHT). */
  frameHeight?: number;
}

/**
 * Pure positioning math for the calendar dropdown. Pulled out of
 * useCalendarPopup so the placement decisions can be unit-tested without
 * a real DOM.
 *
 * The popup adapts to the available space rather than using a fixed size, so it
 * renders fully at any viewport size / browser zoom (PB-2407):
 *   - Width is pinned to `max(anchor, baseMinWidth)` but never wider than the viewport.
 *   - It opens below when the full frame fits there; otherwise it flips to whichever
 *     side has more room, and `maxHeight` caps it to that side so the inner calendar
 *     grid and time list scroll instead of clipping off-screen.
 */
export function computeCalendarDropdownPosition({
  anchorRect,
  viewportWidth,
  viewportHeight,
  baseMinWidth,
  frameHeight = CALENDAR_FRAME_HEIGHT,
}: ComputePositionInput): CalendarDropdownPosition {
  const width = Math.min(Math.max(anchorRect.width, baseMinWidth), viewportWidth - 2 * GAP);
  const maxLeft = viewportWidth - width - GAP;
  const left = Math.min(anchorRect.left, Math.max(GAP, maxLeft));

  const spaceBelow = viewportHeight - anchorRect.bottom - GAP;
  const spaceAbove = anchorRect.top - GAP;
  // Prefer below; flip above only when below can't show the full frame and above has more room.
  const placeAbove = spaceBelow < frameHeight && spaceAbove > spaceBelow;
  const maxHeight = Math.max(0, Math.min(frameHeight, placeAbove ? spaceAbove : spaceBelow));

  if (placeAbove) {
    return { bottom: viewportHeight - anchorRect.top + GAP, left, width, maxHeight };
  }
  return { top: anchorRect.bottom + GAP, left, width, maxHeight };
}

/** Number of years shown per page in the year grid. */
export const YEARS_PER_PAGE = 16;

/** Check whether a date should be disabled (outside bounds or filtered out). */
export function isDateDisabled(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  filterDate?: (d: Date) => boolean
): boolean {
  if (minDate && isBefore(date, startOfDay(minDate))) return true;
  if (maxDate && isAfter(date, startOfDay(maxDate))) return true;
  if (filterDate && !filterDate(date)) return true;
  return false;
}

/** Build an array of dates covering only the rows needed for the given month. */
export function buildCalendarGrid(viewDate: Date): Date[] {
  const firstOfMonth = startOfMonth(viewDate);
  const startWeekday = getDay(firstOfMonth); // 0 = Sunday
  const gridStart = subDays(firstOfMonth, startWeekday);

  // Calculate how many days are in this month
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Total cells = leading offset + days in month, rounded up to full weeks
  const totalCells = Math.ceil((startWeekday + daysInMonth) / COLS) * COLS;

  const days: Date[] = [];
  for (let i = 0; i < totalCells; i++) {
    days.push(addDays(gridStart, i));
  }
  return days;
}

/** Chunk an array into groups of `size`. */
export function chunk<T>(arr: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    rows.push(arr.slice(i, i + size));
  }
  return rows;
}

/** Calculate the first year of the 16-year page containing `year`. */
export function getYearPageStart(year: number): number {
  return Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;
}

/** Build an array of `YEARS_PER_PAGE` years starting from `pageStart`. */
export function buildYearRange(pageStart: number): number[] {
  const years: number[] = [];
  for (let i = 0; i < YEARS_PER_PAGE; i++) {
    years.push(pageStart + i);
  }
  return years;
}

/**
 * Convert a TimeValue to 24-hour format.
 * 12 AM → 0, 1 AM → 1, ..., 11 AM → 11, 12 PM → 12, 1 PM → 13, ..., 11 PM → 23
 */
export function timeValueTo24(time: TimeValue): { hours24: number; minutes: number } {
  let hours24 = time.hours;
  if (time.period === 'AM') {
    hours24 = time.hours === 12 ? 0 : time.hours;
  } else {
    hours24 = time.hours === 12 ? 12 : time.hours + 12;
  }
  return { hours24, minutes: time.minutes };
}

/** Build a TimeValue from a Date. */
export function dateToTimeValue(date: Date): TimeValue {
  const h24 = date.getHours();
  const minutes = date.getMinutes();
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  let hours = h24 % 12;
  if (hours === 0) hours = 12;
  return { hours, minutes, period };
}

/** Apply a TimeValue to a Date, returning a new Date with updated hours/minutes. */
export function applyTimeToDate(date: Date, time: TimeValue): Date {
  const { hours24, minutes } = timeValueTo24(time);
  const result = new Date(date);
  result.setHours(hours24, minutes, 0, 0);
  return result;
}
